<template lang="pug">
#layout.mobile(:class='name')
  .wrapper.centered
    .main-panel(:class="{ 'loading' : loading }")
      .navless-layout-wrapper
        .row.navless-main-body-wrapper
          .container-fluid
            .navless-main-body.col-md-12(ref='currentModule')
              slot
      //- md-footer
      ed-tab-nav.tabs(
        v-if='tabs.length'
        bottom-tabs
        wide
        workflow
        ref='steps'
        @tabSelected='enterStep'
      )
        ed-tab(completed v-for='tab in tabs' :key='tab.label' :title='tab.label')
          component(:is='tab.component')
            span(slot='title') {{ tab.title }}
  //- Receipt capture FAB
  button.receipt-fab(
    v-if='showReceiptFab'
    @click='openReceiptActionSheet'
    aria-label='Submit Receipt'
  )
    i.fas.fa-camera
  //- Action sheet overlay
  .receipt-action-sheet-overlay(
    v-if='showActionSheet'
    @click='closeActionSheet'
  )
    .receipt-action-sheet(@click.stop)
      .action-sheet-card
        .action-sheet-title Submit Receipt
        button.action-sheet-option.action-sheet-primary(@click='openCamera') Open Camera
        button.action-sheet-option(@click='openLibrary') Choose From Library
      button.action-sheet-option.action-sheet-cancel(@click='closeActionSheet') Cancel
  //- Hidden file inputs for camera / library
  input.receipt-file-input(
    ref='cameraInput'
    type='file'
    accept='image/*'
    capture='environment'
    @change='handleReceiptFile'
    style='display:none'
  )
  input.receipt-file-input(
    ref='libraryInput'
    type='file'
    accept='image/*,application/pdf'
    @change='handleReceiptFile'
    style='display:none'
  )
  //- Full-screen overlay while the receipt upload request is in flight.
  //- Keeps the user informed before navigation to /evedpay/submitreceipt
  //- (where the 3-step scan progress card takes over).
  .receipt-upload-overlay(v-if='uploadingReceipt')
    .receipt-upload-card
      i.fas.fa-spinner.fa-spin.receipt-upload-spinner
      .receipt-upload-title Uploading receipt…
      .receipt-upload-subtitle Just a moment.
</template>

<script>

import MdFooter from '@/components/Footer'
import Modules from '@/components/_modules'
import SiteMap from '@/core/site-map'
import { EventBus } from '@/event-bus'
import featureFlags from '@/core/feature-flags/feature-flags'
import receiptScansApi, { NO_RECEIPT_CAPTURE_MODE } from '@/core/api/receiptScans'
import compressReceiptImage from '@/core/receiptScan/imageCompression'

const handleUserScalable = () => {
  const meta = document.getElementById('layout-viewport')
  const hasEmbed = document.querySelector('iframe[srcdoc*="embed"]')
  if (hasEmbed) {
    meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.5, user-scalable=1'
  } else {
    meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0'
  }
}

export default {
  components: {
    MdFooter,
    SiteMap
  },

  mounted() {
    const currentTab = this.$refs.currentModule.children[0].__vue__
    this.tabs = currentTab && currentTab.tabs || []

    handleUserScalable()
  },

  beforeDestroy() {
    // Clean up active poller on component destroy
    if (this.activePoller) {
      this.activePoller.cancel()
      this.activePoller = null
    }
  },

  beforeCreate() {
    const meta = document.createElement('meta')
    meta.id = 'layout-viewport'
    meta.name = 'viewport'
    meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.5, user-scalable=1'
    document.head.appendChild(meta)

    global.config = {
      namespace: 'evedpay',
      layout: 'mobile',
      title: 'EvedPay',
      sitemap: {
        evedpay: {
          Cards: {
            alias: 'cards',
            subtabs: {
              Cards: 'Cards',
              Transactions: 'Transactions'
            }
          }
        }
      },
      enableThemes: true,
      start: '/evedpay'
    }
  },

  data() {
    return {
      name: 'layout-navless',

      source: null,
      description: '',
      tabs: [],
      showActionSheet: false,
      uploadingReceipt: false,
      activePoller: null,
      // No Receipt Capture (PRODUCTDEV-5475): true only when EVERY division the
      // cardholder holds a card in has receipt scanning disabled. Resolved once on
      // created(); gates the FAB. Defaults false so the FAB shows until proven otherwise.
      receiptCaptureDisabled: false,
      // cardId -> DivisionId for the cardholder's cards (PRODUCTDEV-5475). Populated
      // by resolveReceiptCaptureDisabled() from the same cards fetch. Lets a FAB scan
      // launched from a specific card's page scope its match candidates to that card's
      // Division — mirroring the web desktop flow. Empty until resolved (→ broad pool).
      cardDivisionMap: {}
    }
  },

  computed: {
    loading() {
      return this.$loading
    },

    showReceiptFab() {
      if (!featureFlags.receiptScanPrefill) {
        return false
      }
      // No Receipt Capture (PRODUCTDEV-5475): every accessible division disables capture.
      if (this.receiptCaptureDisabled) {
        return false
      }
      if (this.uploadingReceipt || this.showActionSheet) {
        return false
      }
      var path = this.$route.path || ''
      // '/details' does NOT match '/transactiondetails' (no leading '/' between compound word) — list it explicitly.
      var hiddenPaths = ['/settings', '/edit', '/details', '/submitreceipt', '/transactiondetails']
      for (var i = 0; i < hiddenPaths.length; i++) {
        if (path.indexOf(hiddenPaths[i]) !== -1) {
          return false
        }
      }
      return true
    },

    pageName() {
      const layout = this
      let page = ''

      if (this.$route.params.section &&
        Modules[this.$route.params.page + '/' + this.$route.params.section]
      ) {
        page = Modules[this.$route.params.page + '/' + this.$route.params.section]
      } else {
        page = Modules[this.$route.params.page]
      }

      if (page) {
        // When module is async, set description after load
        if (page.module &&
            typeof page.module === 'function' &&
            this.$route.params.action &&
            Modules[this.$route.params.section + '/' + this.$route.params.action]
        ) {
          Modules[this.$route.params.section + '/' + this.$route.params.action]().then((v) => {
            if (v) {
              layout.description = v.description
            }
          })
        }

        return (
                (page.module && typeof page.module !== 'function' && ' ') ||
                (page.module && page.module.description) ||
                (page.module && typeof page.module === 'function' ? '' : '')
        )
      } else {
        return ''
      }
    }
  },

  created () {
    if (this.$route.params.page && this.$route.params.section && this.$route.params.action) {
      this.$routerHistory.save([
        '/' + this.$route.params.namespace + '/' + this.$route.params.page + '/' + this.$route.params.section + '/',
        '/' + this.$route.params.namespace + '/' + this.$route.params.page + '/' + this.$route.params.section + '/' + this.$route.params.action
      ])
    }
    this.source = this.$route.query['back-to'] || this.$routerHistory.previous().path

    EventBus.$off('openAttachment')
    EventBus.$on('openAttachment', () => {
      handleUserScalable()
    })

    EventBus.$off('closeAttachment')
    EventBus.$on('closeAttachment', () => {
      handleUserScalable()
    })

    // Listen for receipt scan completion
    EventBus.$off('receipt-scan-started')
    EventBus.$on('receipt-scan-started', (payload) => {
      this.handleReceiptScanStarted(payload.data)
    })

    // The "Uploading receipt…" overlay covers the full upload + scan flow now
    // that SubmitReceipt no longer renders its own progress card. SubmitReceipt
    // emits 'submit-receipt-ready' the moment it has something to show —
    // candidates (isReady === true) or an error message — at which point we drop
    // the overlay and the user sees the SubmitReceipt page populated.
    EventBus.$off('submit-receipt-ready')
    EventBus.$on('submit-receipt-ready', () => {
      if (this.uploadingReceipt) {
        this.uploadingReceipt = false
      }
    })

    // No Receipt Capture (PRODUCTDEV-5475): only worth resolving when the feature is on
    // (otherwise the FAB is already hidden). Resolves the per-division setting once.
    if (featureFlags.receiptScanPrefill) {
      this.resolveReceiptCaptureDisabled()
    }
  },

  methods: {
    cleanup: function () {
      // this.description = ''
      this.$destroy()
    },
    enterStep: function (tab) {
      //
    },
    openReceiptActionSheet: function () {
      this.showActionSheet = true
    },
    closeActionSheet: function () {
      this.showActionSheet = false
    },
    openCamera: function () {
      this.showActionSheet = false
      this.$refs.cameraInput.value = ''
      this.$refs.cameraInput.click()
    },
    openLibrary: function () {
      this.showActionSheet = false
      this.$refs.libraryInput.value = ''
      this.$refs.libraryInput.click()
    },
    // Division to scope a FAB scan's match candidates to (PRODUCTDEV-5475). When the
    // FAB is launched from a specific card's page the route carries `?cardid=`, so we
    // scope to that card's Division — the suggested transactions then match the web
    // desktop flow, which always uploads within a Division (account.DivisionId) and
    // sends uploadContextLegacyDivisionId. Global tabs (no cardid, or the map not yet
    // resolved) → null → broad pool (unchanged): there is no single Division to mirror.
    currentUploadDivisionId: function () {
      var cardId = this.$route && this.$route.query && this.$route.query.cardid
      if (cardId == null) {
        return null
      }
      var divisionId = this.cardDivisionMap && this.cardDivisionMap[String(cardId)]
      return divisionId != null ? divisionId : null
    },
    handleReceiptFile: async function (event) {
      var self = this
      var raw = event.target.files && event.target.files[0]
      if (!raw) {
        return
      }

      // Canvas-compress images (PDFs pass through). On a phone camera receipt
      // this typically drops the upload from multi-MB to a few hundred KB,
      // which materially cuts the Photon round-trip on mobile networks.
      var file = await compressReceiptImage(raw)

      var formData = new FormData()
      formData.append('file', file)

      var idempotencyKey = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
        ? crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
          var r = (Math.random() * 16) | 0
          var v = c === 'x' ? r : (r & 0x3) | 0x8
          return v.toString(16)
        })

      self.uploadingReceipt = true
      // Mirror the web desktop flow: scope the candidate ranking to the card-in-context's
      // Division when the FAB is launched from a card page (PRODUCTDEV-5475). Omitted on the
      // global tab → broad pool (unchanged). Backend already scopes on this field.
      var uploadDivisionId = self.currentUploadDivisionId()
      var submitExtra = uploadDivisionId != null
        ? { uploadContextLegacyDivisionId: uploadDivisionId }
        : undefined
      receiptScansApi.submitReceipt(formData, idempotencyKey, submitExtra)
        .then(function (data) {
          if (data && data.receiptScanId) {
            // Keep `uploadingReceipt = true` here so the overlay persists across the
            // router transition into /evedpay/submitreceipt. handleReceiptScanStarted's
            // $router.push promise clears it once SubmitReceipt is mounted, eliminating
            // the brief flash between overlay-hide and the SubmitReceipt progress card.
            // extended-bus.js auto-wraps payload as { target, data }; emit raw shape so
            // listener's payload.data resolves to { receiptScanId, source }.
            EventBus.$emit('receipt-scan-started', {
              receiptScanId: data.receiptScanId,
              source: 'mobileFab'
            })
          } else {
            // Upload resolved but the response shape is unexpected (no receiptScanId).
            // Without a toast here the user sees nothing and assumes the tap was lost.
            self.uploadingReceipt = false
            console.error('submitReceipt resolved without receiptScanId', data)
            self.$notifications.notify({
              type: 'danger',
              message: 'Upload returned an unexpected response. Please try again.',
              placement: { from: 'top', align: 'center' }
            })
          }
        })
        .catch(function (error) {
          self.uploadingReceipt = false
          console.error('submitReceipt rejected', error)
          // PRODUCTDEV-5851 — image couldn't be scanned (e.g. dimensions out of range), but the
          // receipt is still persisted (ReadyForMatch, zero candidates) and returns 422 with its
          // receiptScanId. Continue the same post-scan flow silently so the user can match manually
          // or save to folder. Isolated to this one status — every other error keeps its handling.
          if (error.response && error.response.status === 422 && error.response.data && error.response.data.receiptScanId) {
            EventBus.$emit('receipt-scan-started', {
              receiptScanId: error.response.data.receiptScanId,
              source: 'mobileFab'
            })
            return
          }
          if (error.isPlatformSentinelError) {
            // Platform is unreachable or 5xx. Surface this explicitly before the
            // best-effort legacy fallback so the user knows what happened.
            self.$notifications.notify({
              type: 'danger',
              message: 'Platform unavailable — falling back to legacy upload.',
              placement: { from: 'top', align: 'center' }
            })
            self.fallbackLegacyUpload(file)
            return
          }
          self.$notifications.notify({
            type: 'danger',
            placement: { from: 'top', align: 'center' },
            timeout: 5000,
            message: 'Failed to upload receipt. Please try again.'
          })
        })
    },
    fallbackLegacyUpload: function (file) {
      var self = this
      var formData = new FormData()
      formData.append('attachment_upload', file)
      var legacyUrl = self.api ? self.api.env + self.api.$.evedpaycard.receipt : null
      if (!legacyUrl) {
        self.$notifications.notify({
          type: 'danger',
          message: 'Legacy upload endpoint not configured. Receipt was not saved.',
          placement: { from: 'top', align: 'center' }
        })
        return
      }
      var token = self.$user && self.$user.sessionSettings ? self.$user.sessionSettings.Token : ''
      self.api.post(legacyUrl, formData, {
        headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'multipart/form-data' }
      })
        .then(function () {
          self.$notifications.notify({
            type: 'info',
            message: 'Receipt uploaded via legacy fallback. Scanning is unavailable.',
            placement: { from: 'top', align: 'center' }
          })
        })
        .catch(function (legacyError) {
          console.error('legacy receipt upload failed', legacyError)
          self.$notifications.notify({
            type: 'danger',
            message: 'Legacy upload failed. Receipt was not saved.',
            placement: { from: 'top', align: 'center' }
          })
        })
    },
    // Handle receipt scan started event. Camera / Library uploads navigate
    // immediately to SubmitReceipt, where the 3-step progress card and poller live.
    // The uploadingReceipt overlay is intentionally held over the route transition —
    // the $route watcher below clears it once the route flips, so the user never sees
    // a blank Cards-page flash between the overlay and the SubmitReceipt progress card.
    handleReceiptScanStarted: function (data) {
      if (this.activePoller) {
        this.activePoller.cancel()
        this.activePoller = null
      }
      var self = this
      this.$router.push({
        path: '/evedpay/submitreceipt',
        query: { receiptScanId: data.receiptScanId }
      })
      // Safety net: matches the poller's 60s POLL_TIMEOUT_MS plus a 30s buffer
      // for slow Photon round-trips. If SubmitReceipt never emits the ready event
      // (failed mount, navigation rejected, etc.) the overlay force-clears so it
      // never gets stuck on screen forever.
      setTimeout(function () { self.uploadingReceipt = false }, 90000)
    },
    // No Receipt Capture (PRODUCTDEV-5475): hide the FAB only when EVERY division the
    // cardholder holds a card in disables capture. If any division still allows it we
    // keep the FAB — the floating receipt's target division isn't known until it's
    // matched, so we never suppress a legitimate flow. The per-division mode is read
    // from Platform (cardholder-readable; a missing row means capture-enabled). Any
    // fetch failure leaves the FAB visible (safe default).
    resolveReceiptCaptureDisabled: function () {
      var self = this
      if (!this.api || !this.api.$ || !this.api.$.evedpaycard || !this.api.forms) {
        return
      }
      return this.api.forms.post(this.api.$.evedpaycard.cards, {
        sort: 'CardGroup',
        dir: 'DESC',
        start: 0,
        limit: 100,
        isLiveGrid: true,
        filter: { searchTerm: '' }
      }).then(function (r) {
        var cards = (r && r.data && Array.isArray(r.data.Data)) ? r.data.Data : []
        // Cache cardId -> DivisionId so a FAB scan launched from a specific card's page
        // can scope its match candidates to that card's Division (PRODUCTDEV-5475),
        // mirroring the web desktop flow. The route's `cardid` equals the card's Id.
        var divMap = {}
        cards.forEach(function (card) {
          if (card && card.Id != null && card.DivisionId != null) {
            divMap[String(card.Id)] = card.DivisionId
          }
        })
        self.cardDivisionMap = divMap
        var divisionIds = []
        cards.forEach(function (card) {
          if (card.DivisionId != null && divisionIds.indexOf(card.DivisionId) === -1) {
            divisionIds.push(card.DivisionId)
          }
        })
        if (!divisionIds.length) {
          return
        }
        return Promise.all(divisionIds.map(function (divisionId) {
          return receiptScansApi.getReceiptScanSetting(divisionId)
            .then(function (setting) { return setting ? setting.prefillMode : null })
            .catch(function () { return null })
        })).then(function (modes) {
          var resolved = modes.filter(function (m) { return m != null })
          // Hide only when every division resolved AND all are no-capture.
          self.receiptCaptureDisabled =
            resolved.length === divisionIds.length &&
            resolved.length > 0 &&
            resolved.every(function (m) { return m === NO_RECEIPT_CAPTURE_MODE })
        })
      }).catch(function () {
        // Card fetch failed — leave the FAB visible.
      })
    }
  }
}

</script>

<style scoped lang='scss'>
@import "~@/assets/sass/_variables.scss";

.receipt-fab {
  position: fixed;
  bottom: 5rem;
  right: 1.5rem;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #4A90E2;
  color: #fff;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 90;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:active {
    transform: scale(0.92);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }
}

.receipt-action-sheet-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 95;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.receipt-upload-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.55);
  z-index: 97;
  display: flex;
  align-items: center;
  justify-content: center;
}

.receipt-upload-card {
  width: 80%;
  max-width: 280px;
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem 1.25rem;
  text-align: center;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
}

.receipt-upload-spinner {
  font-size: 28px;
  color: #1976d2;
  margin-bottom: 0.75rem;
}

.receipt-upload-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 0.25rem;
}

.receipt-upload-subtitle {
  font-size: 13px;
  color: #6b6b6b;
}

.receipt-action-sheet {
  width: 100%;
  max-width: 420px;
  padding: 0 8px env(safe-area-inset-bottom, 16px);
  display: flex;
  flex-direction: column;
}

.action-sheet-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
}

.action-sheet-title {
  padding: 14px 20px;
  font-size: 13px;
  font-weight: 500;
  color: #6b6b6b;
  text-align: center;
  border-bottom: 1px solid #e5e5e5;
}

.action-sheet-option {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 14px 20px;
  border: none;
  background: #fff;
  font-size: 16px;
  color: #4A90E2;
  cursor: pointer;
  text-align: center;

  &:active {
    background: #f0f0f0;
  }
}

.action-sheet-card .action-sheet-option + .action-sheet-option {
  border-top: 1px solid #e5e5e5;
}

.action-sheet-cancel {
  margin-top: 8px;
  background: #fff;
  border-radius: 12px;
  color: #4A90E2;
  font-weight: 700;
  justify-content: center;
  padding: 16px 20px;
}

#layout.mobile >>> .loading.main-panel {
  &:before, &:after {
    display: none;
  }
}

#layout.mobile >>> .loading:after {
  background: #fff;
}

// Was 260px — a stale reservation that let the user scroll a near-viewport's worth
// past the actual page content on every mobile route. Each EvedPay page already pads
// itself for the 56px fixed tab bar, so the global
// reservation is dead weight. Collapsed to 0.
#layout.mobile >>> .module {
  padding-bottom: 0;
}

body {
  @include theme() { background: v(background) }
  height: 100vh;
}

body .navless-layout-wrapper,
body .navless-main-body-wrapper {
  min-height: 100%;
  padding: 0;
}

.main-panel .content.module {
  min-height: calc(100% - 123px);
}

.logo {
  display: inline-block;
  padding-bottom: .75rem;
  min-height: 50px;
  background-position: 10px 100%;
  margin-top: 14px;
}

.container-fluid {
  position: relative;
}

.navless-layout-wrapper {
  @include theme() { background: v(canvas) }
  padding-bottom: 1rem;
  //height: 100vh;

  >>> .dawn-modal-container {
    width: 100%;
    margin-top: 2rem;
  }

  >>> .navless-layout-header {
    margin-left: -16px;
    margin-right: -16px;
    padding: 0.2rem 1rem !important;
    max-height: 2rem;

    a {
      color: #fff !important;
    }
  }
  .navless-layout-header, >>> .navless-layout-header {
    padding: $side-padding;
    position: relative;
    height: 5rem;

    .navless-page-name {
      display: inline-block;
      position: absolute;
      left: 11rem;
      top: 1.2rem;

      h2 {
        margin: 0;
        font-size: $font-size-large;
      }
    }

    @include theme() {
    background: v(primary-04);
    color: v(canvas);
    }

    .navless-page-close {
      text-align: right;
      position: absolute;
      right: $side-padding;
      bottom: 1.7rem;
      text-transform: uppercase;
      font-size: $font-size-xsmall;

      a {
        @include theme() { color: v(canvas) }
        transition: all .2s ease-in-out;
        &:hover {
          opacity: .3;
          text-decoration: none;
        }
        i {
          font-size: $font-size-xlarge;
          margin-left: .1rem;
          vertical-align: middle;
          margin-top: -0.15rem;
        }
      }
    }
  }
}

.navless-main-body-wrapper {
  margin: 0;
}

.navless-main-body {
  @include theme() { background: v(canvas) }
  padding: 0rem 1rem;
}

#layout.mobile {
  // The shared dashboard styles make .main-panel a viewport-height inner scroller.
  // In the native shell that leaves the page a fixed box whose height is derived from
  // the browser viewport rather than the webview's, so let the document scroll instead.
  .main-panel {
    height: auto;
    max-height: none;
    overflow: visible;
  }
  .navless-layout-header {
    background: #4e2683;
    > .container-fluid {
      text-align: center;
    }
  }
  .logo {
    background-image: none;
    margin: 0 auto;
    width: auto;
    img {
      display: block;
      height: 70%;
    }
  }
  .navless-layout-wrapper {
    // min-height: calc(100% - 3rem);
    min-height: 0;
  }
  .tabs {
    // height: calc(100% - 8rem);
    height: calc(100% - 5.5rem);
    >>> .dawn-tab-content {
      height: calc(100% - 0rem);
      padding-top: 0.5rem;
      overflow: auto;
    }
    >>> .dawn-tab-nav {
      display: none;
      padding-top: 0.75rem;
      li {
        text-align: center;
      }
    }
  }
}

</style>



// WEBPACK FOOTER //
// src/components/Layouts/mobile.vue